import { Box, Button, Grid, MenuItem, Paper, TextField, Typography } from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { CheckCircle, PhoneIphone, Save } from "@material-ui/icons"
import React, { useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import type {
    AplicativoCriado,
    NovoAplicativoPayload,
    SituacaoAplicativo,
} from "../../services/aplicativoEstabelecimento"
import {
    criarAplicativoEstabelecimento,
    obterSituacaoAplicativo,
} from "../../services/aplicativoEstabelecimento"
import useLoadingStore from "../../stores/LoadingStore"
import { useSnackbar } from "../../utils/notificacoes"
import Cabecalho from "../Cabecalho"
import { Loading } from "../RegistradorDePedidos"

// Mesmas categorias do cadastro de estabelecimento do sistemaadm.
const CATEGORIAS = [
    "Variado",
    "Restaurante",
    "Lanchonete",
    "Hamburgueria",
    "Pizzaria",
    "Açaiteria",
    "Assados",
    "Doceria",
    "Sorveteria",
    "Comida Oriental",
    "Supermercado",
    "MarketPlace",
    "Outros",
]

const FORM_INICIAL: NovoAplicativoPayload = {
    nome: "",
    categoria: "",
    cnpj: "",
    nomeResponsavel: "",
    emailResponsavel: "",
    corPrimaria: "#E74A3B",
    corSecundaria: "#EFC500",
}

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "800px",
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
    },
    card: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
    },
    cardHeaderTitle: { fontWeight: 600, color: "#2c3e50" },
    cardContent: { padding: theme.spacing(3) },
    iconHeader: { color: "#4caf50", marginRight: theme.spacing(1) },
    corRow: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
    corAmostra: {
        width: 40,
        height: 40,
        padding: 0,
        border: "1px solid #ccc",
        borderRadius: 8,
        cursor: "pointer",
        background: "none",
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        padding: theme.spacing(1.5, 4),
        fontWeight: 600,
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
    },
    hint: { color: "#7f8c8d", fontSize: "0.85rem" },
}))

const COR_VALIDA = /^#[0-9A-Fa-f]{6}$/

export default function CriarAplicativo() {
    const classes = useStyles()
    const history = useHistory()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)

    const [situacao, setSituacao] = useState<SituacaoAplicativo | null>(null)
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const [form, setForm] = useState<NovoAplicativoPayload>(FORM_INICIAL)
    const [criado, setCriado] = useState<AplicativoCriado | null>(null)
    const [salvando, setSalvando] = useState(false)

    useEffect(() => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        obterSituacaoAplicativo(autorizacao)
            .then(resposta => {
                if ("retornoErro" in resposta) {
                    setErroCarregamento(resposta.mensagem || "Erro ao consultar o aplicativo")
                    return
                }
                setSituacao(resposta)
                const s = resposta.sugestao
                if (s) {
                    setForm({
                        nome: s.nome ?? "",
                        categoria:
                            s.categoria && CATEGORIAS.includes(s.categoria) ? s.categoria : "",
                        cnpj: s.cnpj ?? "",
                        nomeResponsavel: s.nomeResponsavel ?? "",
                        emailResponsavel: s.emailResponsavel ?? "",
                        corPrimaria: s.corPrimaria || FORM_INICIAL.corPrimaria,
                        corSecundaria: s.corSecundaria || FORM_INICIAL.corSecundaria,
                    })
                }
            })
            .finally(() => setLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const setCampo =
        (campo: keyof NovoAplicativoPayload) => (e: React.ChangeEvent<{ value: unknown }>) => {
            const valor = String(e.target.value)
            setForm(f => ({ ...f, [campo]: valor }))
        }

    const handleCriar = async () => {
        if (!form.nome.trim()) {
            enqueueSnackbar("Informe o nome do aplicativo.", { variant: "error" })
            return
        }
        if (!COR_VALIDA.test(form.corPrimaria) || !COR_VALIDA.test(form.corSecundaria)) {
            enqueueSnackbar("Use cores no formato #RRGGBB.", { variant: "error" })
            return
        }
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setSalvando(true)
        setLoading(true)
        try {
            const resposta = await criarAplicativoEstabelecimento(autorizacao, {
                ...form,
                nome: form.nome.trim(),
            })
            if ("retornoErro" in resposta) {
                enqueueSnackbar(resposta.mensagem || "Erro ao criar o aplicativo", {
                    variant: "error",
                })
                return
            }
            setCriado(resposta)
            enqueueSnackbar("Aplicativo criado com sucesso!", { variant: "success" })
        } finally {
            setSalvando(false)
            setLoading(false)
        }
    }

    const campoCor = (campo: "corPrimaria" | "corSecundaria", label: string) => (
        <Box className={classes.corRow}>
            <input
                type="color"
                className={classes.corAmostra}
                value={COR_VALIDA.test(form[campo]) ? form[campo] : "#000000"}
                onChange={e => {
                    const valor = e.target.value.toUpperCase()
                    setForm(f => ({ ...f, [campo]: valor }))
                }}
                aria-label={label}
                disabled={salvando}
            />
            <TextField
                label={label}
                variant="outlined"
                size="small"
                fullWidth
                value={form[campo]}
                onChange={setCampo(campo)}
                error={!COR_VALIDA.test(form[campo])}
                disabled={salvando}
            />
        </Box>
    )

    const renderConteudo = () => {
        if (erroCarregamento) {
            return <Typography color="error">{erroCarregamento}</Typography>
        }
        if (!situacao) return null

        if (criado || situacao.possuiAplicativo) {
            const appNome = criado?.appNome ?? situacao.appNome
            const url = criado?.urlAcesso ?? situacao.urlAcesso
            return (
                <Paper className={classes.card}>
                    <Box className={classes.cardHeader}>
                        <CheckCircle className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            {criado ? "Aplicativo criado" : "Estabelecimento já possui aplicativo"}
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Typography gutterBottom>
                            Aplicativo: <strong>{appNome}</strong>
                        </Typography>
                        {url && (
                            <Typography gutterBottom>
                                Site:{" "}
                                <a
                                    href={`https://${url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {url}
                                </a>
                            </Typography>
                        )}
                        <Box mt={2}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => history.push("/servicos/modulo/editar-aplicativo")}
                            >
                                Editar aplicativo
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )
        }

        if (!situacao.podeCriar) {
            return (
                <Typography color="error">
                    Você não tem permissão para criar aplicativo para este estabelecimento.
                </Typography>
            )
        }

        return (
            <>
                <Paper className={classes.card}>
                    <Box className={classes.cardHeader}>
                        <PhoneIphone className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Dados do aplicativo
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Typography className={classes.hint} style={{ marginBottom: 16 }}>
                            O aplicativo será criado e associado a este estabelecimento, que passa a
                            ser o estabelecimento principal dele. O endereço do site é gerado a
                            partir do nome.
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                                <TextField
                                    label="Nome do aplicativo"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    required
                                    value={form.nome}
                                    onChange={setCampo("nome")}
                                    disabled={salvando}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    label="Categoria"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={form.categoria}
                                    onChange={setCampo("categoria")}
                                    disabled={salvando}
                                >
                                    {CATEGORIAS.map(c => (
                                        <MenuItem key={c} value={c}>
                                            {c}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="CNPJ/CPF"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={form.cnpj}
                                    onChange={setCampo("cnpj")}
                                    disabled={salvando}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Nome do responsável"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={form.nomeResponsavel}
                                    onChange={setCampo("nomeResponsavel")}
                                    disabled={salvando}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="E-mail do responsável"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="email"
                                    value={form.emailResponsavel}
                                    onChange={setCampo("emailResponsavel")}
                                    disabled={salvando}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {campoCor("corPrimaria", "Cor principal")}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {campoCor("corSecundaria", "Cor secundária")}
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
                <Box display="flex" justifyContent="center">
                    <Button
                        variant="contained"
                        className={classes.saveButton}
                        startIcon={<Save />}
                        onClick={handleCriar}
                        disabled={salvando}
                    >
                        Criar aplicativo
                    </Button>
                </Box>
            </>
        )
    }

    return (
        <div>
            <Cabecalho pagina="Criar Aplicativo" />
            <Loading />
            <div className={classes.container}>
                <div className={classes.content}>
                    <Box mb={3}>
                        <Typography variant="h4" className={classes.titleText}>
                            Criar aplicativo
                        </Typography>
                        <Typography variant="subtitle1" style={{ color: "#7f8c8d" }}>
                            Aplicativo e site próprios para este estabelecimento
                        </Typography>
                    </Box>
                    {renderConteudo()}
                </div>
            </div>
        </div>
    )
}
